import * as React from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import { productModelToDetails } from "../../../../../models/Product.model";
import { ProductDetailDrawer } from "./product-detail-drawer";
import { WizardShell, SearchField, AppModal } from "../../../../../components/ui";
import { TOPIC_STEPS } from "../../wizard-steps";
import { AnnotationDialog } from "../../../actives/annotation.dialog";
import { Loading } from "../../../../../components/loading";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import { useProducts } from "./use-products";
import ProductsGrid from "./components/ProductsGrid";
import SelectionChip from "../../components/selection-chip";

type Props = {
    onFinish: () => void;
    onAddMoreItem: () => void;
};

export const Products: React.FC<Props> = ({ onFinish, onAddMoreItem }) => {
    const {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        value,
        open,
        setOpen,
        filter,
        setFilter,
        filterKey,
        setFilterKey,
        selectedProduct,
        annotationOpen,
        setAnnotationOpen,
        activeSelected,
        onPopUp,
        selectedCount,
        selectedIds,
        clearAll,
        visibleProducts,
        handleChange,
        handleClickProduct,
        handleSelectProduct,
        handleAddMoreItems,
        handleContinue,
        handleClickOpen,
        saveAnnotation,
        popUpOpen,
        handleClose,
        handleFavorite,
    } = useProducts(onFinish, onAddMoreItem);

    return (
        <>
            <Loading open={loading} />
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <ProductDetailDrawer
                open={open}
                detail={productModelToDetails(selectedProduct)}
                onClose={() => setOpen(false)}
                onFavorite={() => handleFavorite(selectedProduct)}
            />

            {annotationOpen && (
                <AnnotationDialog
                    annotation={activeSelected?.annotation}
                    open={annotationOpen}
                    onSaveAnnotation={saveAnnotation}
                    onClose={() => setAnnotationOpen(false)}
                />
            )}

            {onPopUp && (
                <AppModal open={onPopUp} onClose={handleClose} kicker="Aviso" title="Opção não disponível" maxWidth={460}>
                    <Typography sx={{ fontSize: "14.5px", color: "text.secondary", lineHeight: 1.6 }}>
                        Você não pode fazer anotações no ativo selecionado. Volte para a listagem de todos ativos para incluir anotações.
                    </Typography>
                </AppModal>
            )}

            <WizardShell
                steps={TOPIC_STEPS as unknown as string[]}
                activeIndex={2}
                onBack={handleAddMoreItems}
                onNext={handleContinue}
                nextLabel="Continuar"
                nextDisabled={selectedCount === 0}
            >
                {/* Cabeçalho */}
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", mb: "4px" }}>
                    <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400, color: "text.primary" }}>
                        Ativos
                    </Typography>
                    <SelectionChip count={selectedCount} onClear={clearAll} />
                </Box>
                <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: "16px" }}>
                    Selecione os ativos para a formulação.
                </Typography>

                {/* Tabs */}
                <Tabs
                    value={value}
                    onChange={handleChange}
                    sx={{
                        mb: "12px",
                        maxWidth: "100%",
                        "& .MuiTab-root": { fontSize: "13.5px", fontWeight: 600, textTransform: "none", minHeight: 40 },
                        "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 2 },
                        "@media (max-width: 360px)": {
                            "& .MuiTab-root": { minWidth: 0, flex: 1, px: 0.5, fontSize: "12px" },
                        },
                    }}
                >
                    <Tab label="Todos" />
                    <Tab label="Favoritos" />
                    <Tab label="Selecionados" />
                </Tabs>

                {/* Filtros */}
                <Box
                    sx={{
                        display: "flex",
                        gap: "12px",
                        mb: "12px",
                        alignItems: "center",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: "background.paper",
                        pt: "4px",
                        pb: "4px",
                        "@media (max-width: 360px)": {
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: "8px",
                        },
                    }}
                >
                    <FormControl sx={{ flex: "0 0 160px", "@media (max-width: 360px)": { flex: "1 1 auto", width: "100%" } }} size="small">
                        <InputLabel id="filter-key-label">Filtrar por</InputLabel>
                        <Select value={filterKey} label="Filtrar por" labelId="filter-key-label" onChange={(e) => setFilterKey(e.target.value)}>
                            <MenuItem value="productName">Nome</MenuItem>
                            <MenuItem value="annotation">Anotação</MenuItem>
                            <MenuItem value="description">Descrição</MenuItem>
                            <MenuItem value="mechanismAction">Mecanismo de ação</MenuItem>
                            <MenuItem value="incompatibility">Incompatibilidade</MenuItem>
                        </Select>
                    </FormControl>
                    <SearchField value={filter} onChange={setFilter} placeholder="Buscar..." sx={{ flex: 1, "@media (max-width: 360px)": { width: "100%" } }} />
                </Box>

                {/* Lista — as três abas usam o mesmo grid e as mesmas ações;
                    só muda a origem dos itens (o wrapper flex antigo encolhia o grid) */}
                <ProductsGrid
                    products={visibleProducts}
                    selectedIds={selectedIds}
                    onClick={handleClickProduct}
                    onPopUp={popUpOpen}
                    onSelect={handleSelectProduct}
                    annotationOpen={handleClickOpen}
                    onFavorite={handleFavorite}
                />
            </WizardShell>
        </>
    );
};
